import { z } from 'zod'

import {
  EMISSION_OPENING_EVENT_TYPES,
  METHOD,
  RECEPTION_OPENING_EVENT_TYPES,
} from '../types/openingEvents.types'

// ─────────────────────────────────────────────────────────────────────────── //
//                         RECEPTION OPENING EVENTS                           //
//                              Kiosk → Galadrim                              //
// ─────────────────────────────────────────────────────────────────────────── //

// ─── method.selected ─────────────────────────────────────────────────────── //

const methodSelectedDataSchema = z.object({
  method: z.enum([METHOD.ACCOUNT, METHOD.CARD, METHOD.PREAUTHORIZATION]),
})

export type MethodSelectedEventData = z.infer<typeof methodSelectedDataSchema>

// ─── method.verified ─────────────────────────────────────────────────────── //

const methodVerifiedDataSchema = z.discriminatedUnion('method', [
  z.object({
    method: z.literal(METHOD.ACCOUNT),
    accountId: z.number(),
    valid: z.boolean(),
  }),
  z.object({
    method: z.literal(METHOD.CARD),
    valid: z.boolean(),
  }),
  z.object({
    method: z.literal(METHOD.PREAUTHORIZATION),
    valid: z.boolean(),
    contactless: z.boolean(),
    preAuthorizationId: z.string().optional(),
    preAuthorizationAmount: z.number().optional(),
  }),
])

export type MethodVerifiedEventData = z.infer<typeof methodVerifiedDataSchema>

// ─── opening.report ──────────────────────────────────────────────────────── //

const openingReportDataSchema = z.object({
  granted: z.boolean(),
  timeout: z.boolean(),
  method: z.enum([METHOD.ACCOUNT, METHOD.CARD, METHOD.PREAUTHORIZATION]),
  valid: z.boolean(),
  contactless: z.boolean().optional(),
  preAuthorizationId: z.string().optional(),
  preAuthorizationAmount: z.number().optional(),
})

export type OpeningReportEventData = z.infer<typeof openingReportDataSchema>

// ─── Reception event (discriminated union) ───────────────────────────────── //

export const receptionOpeningEventSchema = z.discriminatedUnion('type', [
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.METHOD_SELECTED),
    data: methodSelectedDataSchema,
  }),
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.METHOD_VERIFIED),
    data: methodVerifiedDataSchema,
  }),
  z.object({
    time: z.string(),
    type: z.literal(RECEPTION_OPENING_EVENT_TYPES.OPENING_REPORT),
    data: openingReportDataSchema,
  }),
])

export type ReceptionOpeningEvent = z.infer<typeof receptionOpeningEventSchema>

// ─────────────────────────────────────────────────────────────────────────── //
//                          EMISSION OPENING EVENTS                           //
//                              Galadrim → Kiosk                              //
// ─────────────────────────────────────────────────────────────────────────── //

// ─── method.confirm or opening.confirm ───────────────────────────────────── //

export const confirmMethodSchema = z.object({
  granted: z.boolean(),
  method: z.enum([METHOD.ACCOUNT, METHOD.CARD, METHOD.PREAUTHORIZATION]),
})

export type ConfirmMethodData = z.infer<typeof confirmMethodSchema>

export const confirmMethodRequestSchema = confirmMethodSchema.extend({
  openingEventId: z.number().int().positive(),
})

export type ConfirmMethodRequestData = z.infer<typeof confirmMethodRequestSchema>

const methodConfirmDataSchema = confirmMethodSchema.extend({
  manual: z.boolean(),
})

// ─── Emission event (discriminated union) ────────────────────────────────── //

export const emissionOpeningEventSchema = z.discriminatedUnion('type', [
  z.object({
    time: z.string(),
    type: z.enum([
      EMISSION_OPENING_EVENT_TYPES.METHOD_CONFIRM,
      EMISSION_OPENING_EVENT_TYPES.OPENING_CONFIRM,
    ]),
    data: methodConfirmDataSchema,
  }),
])

export type EmissionOpeningEvent = z.infer<typeof emissionOpeningEventSchema>
